import { useRef, useState } from 'react';

import { Portal } from '@skyscanner-internal/backpack-web/bpk-react-utils';

import { useMBDContext } from '../../common/MBD/useMBDContext';
import DotMarker from '../Markers/DotMarker';
import PriceMarker from '../Markers/PriceMarker';
import PriceMarkerV2 from '../Markers/PriceMarkerV2';
import ProviderCard from '../Popups/DotMarkerPopup';
import HotelMarkerPopup from '../Popups/HotelMarkerPopup';
import PriceMarkerPopup from '../Popups/PriceMarkerPopup';
import MARKERS_DATA_SOURCE from '../types';

import type {
  DynamicMapStrings,
  MarkersDataSource,
  MapMarker,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/DynamicMapProps';

import STYLES from './MapMobileMarker.module.scss';

type MapMobileMarkerProps = {
  renderTarget?: () => HTMLElement | null;
  index: number;
  location: MapMarker;
  onSelected: () => void;
  selectedIndex?: number;
  strings: DynamicMapStrings;
  priceMarkerVersion: string; // 'v1' for PriceMarker, 'v2' for PriceMarkerV2
};

const getMarker = (
  markersDataSource: MarkersDataSource,
  location: MapMarker,
  priceMarkerVersion: string = 'v2',
) => {
  if (
    markersDataSource ===
    MARKERS_DATA_SOURCE.SUPPLIER_AND_PICK_UP_LOCATION_ADDRESS_WITH_PRICE_FROM_DEAL
  ) {
    return (
      <PriceMarker
        price={location.price ?? ''}
        locationId={location.entityId}
        onLocationClicked={() => {}}
      />
    );
  }
  if (
    markersDataSource === MARKERS_DATA_SOURCE.HOTELS_DEAL_INFORMATION_FROM_DEAL
  ) {
    return priceMarkerVersion === 'v2' ? (
      <PriceMarkerV2
        price={location.price ?? ''}
        locationId={location.entityId}
        onLocationClicked={() => {}}
      />
    ) : (
      <PriceMarker
        price={location.price ?? ''}
        locationId={location.entityId}
        onLocationClicked={() => {}}
      />
    );
  }

  return <DotMarker />;
};

const getMarkerPopup = (
  markersDataSource: MarkersDataSource,
  location: MapMarker,
  strings: DynamicMapStrings,
  index: number,
  setIsOpen: (isOpen: boolean) => void,
) => {
  switch (markersDataSource) {
    case MARKERS_DATA_SOURCE.SUPPLIER_AND_PICK_UP_LOCATION_ADDRESS_WITH_PRICE_FROM_DEAL:
      return (
        <PriceMarkerPopup
          index={index}
          key={`mapMarker-popup-${index}`}
          redirectUrl={location.redirectUrl ?? ''}
          address={location.address ?? ''}
          logos={location.logos}
          strings={strings}
          popupLinkStr={location.popupLinkStr ?? ''}
          onClose={() => setIsOpen(false)}
        />
      );
    case MARKERS_DATA_SOURCE.HOTELS_DEAL_INFORMATION_FROM_DEAL:
      if (
        !location.hotelImageAltText ||
        !location.price ||
        !location.priceTrailingText
      ) {
        return null;
      }
      return (
        <HotelMarkerPopup
          index={index}
          hotelName={location.entityName}
          redirectUrl={location.redirectUrl ?? ''}
          price={location.price}
          priceTrailingText={location.priceTrailingText}
          hotelImageAltText={location.hotelImageAltText}
          hotelImageUrl={location.hotelImageUrl}
          hotelReviewsData={location.hotelReviewsData}
          hotelRatingData={location.hotelRatingData}
          onClose={() => setIsOpen(false)}
        />
      );
    default:
      return (
        <ProviderCard
          index={index}
          key={`mapMarker-popup-${index}`}
          redirectUrl={location.redirectUrl ?? ''}
          entityName={location.entityName}
          logos={location.logos}
          strings={strings}
          address={location.address}
          onClose={() => setIsOpen(false)}
          markersDataSource={markersDataSource}
        />
      );
  }
};

const MapMobileMarker = ({
  index,
  location,
  onSelected,
  priceMarkerVersion,
  renderTarget = () => null,
  selectedIndex = -1,
  strings,
}: MapMobileMarkerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { buildMBDParam, rawComponentName } = useMBDContext();

  return (
    <>
      <span
        ref={useRef(null)}
        onClick={() => {
          onSelected();
          setIsOpen(true);
        }}
        role="presentation"
      >
        <div
          className={STYLES.MapMobileMarker__clusterContainer}
          data-tracking-element-id={`${rawComponentName}_marker_${index}`}
          data-tracking-common-params={buildMBDParam(
            `${rawComponentName}_marker`,
          )}
        >
          {getMarker(location.markersDataSource, location, priceMarkerVersion)}
        </div>
      </span>
      <Portal
        isOpen={isOpen && selectedIndex === index}
        renderTarget={renderTarget}
      >
        <div
          className={STYLES.MapMobileMarker__card}
          data-tracking-element-id={`${rawComponentName}_popup_${index}`}
          data-tracking-common-params={buildMBDParam(
            `${rawComponentName}_popup`,
          )}
        >
          {getMarkerPopup(
            location.markersDataSource,
            location,
            strings,
            index,
            setIsOpen,
          )}
        </div>
      </Portal>
    </>
  );
};

export default MapMobileMarker;
